// ==============================================
// Clubs Listing Page
// ==============================================

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Club } from '../types';
import { clubsAPI } from '../services/api';
import { clubPath } from '../utils/slug';

// ── Sub-components ──────────────────────────────────────────

const StarRating: React.FC<{ rating: number; reviews: number }> = ({ rating, reviews }) => (
  <div className="d-flex align-items-center gap-1" style={{ fontSize: '.8rem' }}>
    <span style={{ color: '#EF8628' }}>★</span>
    <span style={{ fontWeight: 700, color: 'var(--tt-text)' }}>{rating.toFixed(1)}</span>
    <span style={{ color: 'var(--tt-text-muted)' }}>({reviews})</span>
  </div>
);

const AvailabilityBar: React.FC<{ available: number; total: number }> = ({ available, total }) => {
  const pct = total > 0 ? Math.round((available / total) * 100) : 0;
  const color = pct === 0 ? '#dc2626' : pct <= 30 ? '#f59e0b' : '#16a34a';
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '.72rem' }}>
        <span style={{ color, fontWeight: 600 }}>
          {available === 0 ? 'Complet azi' : `${available} din ${total} mese libere`}
        </span>
      </div>
      <div style={{ height: 5, background: 'var(--tt-border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .3s' }} />
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────

const Clubs: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState('Toate orașele');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  useEffect(() => {
    clubsAPI.getAll()
      .then(res => { if (res.data?.clubs) setClubs(res.data.clubs); })
      .catch(() => setError('Nu s-au putut încărca cluburile.'))
      .finally(() => setLoading(false));
  }, []);

  const cities = useMemo(() => {
    const unique = Array.from(new Set(clubs.map(c => c.city_name).filter(Boolean))) as string[];
    return ['Toate orașele', ...unique.sort()];
  }, [clubs]);

  const filtered = useMemo(() => clubs.filter(club => {
    const city = club.city_name || '';
    const avail = club.number_of_tables - (club.booked_tables_today ?? 0);
    return (
      (selectedCity === 'Toate orașele' || city === selectedCity) &&
      (
        club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (club.address || '').toLowerCase().includes(searchTerm.toLowerCase())
      ) &&
      (!onlyAvailable || avail > 0)
    );
  }), [clubs, selectedCity, searchTerm, onlyAvailable]);

  const hasActiveFilters = searchTerm || selectedCity !== 'Toate orașele' || onlyAvailable;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="container page-inner">

        <div className="page-header">
          <h2>Cluburi de tenis de masă</h2>
          <p>Găsește și rezervă mese la cluburi în apropierea ta</p>
        </div>

        {/* Filter bar */}
        <div style={{
          background: 'var(--tt-surface)', border: '1px solid var(--tt-border)',
          borderRadius: 16, padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
          boxShadow: 'var(--sh-sm)',
        }}>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <svg style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--tt-text-muted)', pointerEvents: 'none' }}
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text" className="form-control force-p-left"
              placeholder="Caută după nume, oraș sau adresă…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', paddingRight: searchTerm ? '2.5rem' : undefined, borderRadius: 10, height: 44 }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
                borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', color: 'var(--tt-text-muted)', fontSize: '.7rem',
              }}>✕</button>
            )}
          </div>

          {/* City pills + toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.625rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              {cities.map(city => {
                const active = selectedCity === city;
                return (
                  <button key={city} onClick={() => setSelectedCity(city)} style={{
                    padding: '.35rem .9rem', borderRadius: 999,
                    border: `1.5px solid ${active ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                    background: active ? 'var(--tt-primary)' : 'var(--tt-surface)',
                    color: active ? '#fff' : 'var(--tt-text-2)',
                    fontSize: '.8rem', fontWeight: active ? 600 : 400,
                    cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
                  }}>{city}</button>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexShrink: 0 }}>
              <button onClick={() => setOnlyAvailable(v => !v)} style={{
                display: 'flex', alignItems: 'center', gap: '.5rem',
                padding: '.35rem .9rem', borderRadius: 999,
                border: `1.5px solid ${onlyAvailable ? '#15803d' : 'var(--tt-border)'}`,
                background: onlyAvailable ? '#f0fdf4' : 'var(--tt-surface)',
                color: onlyAvailable ? '#15803d' : 'var(--tt-text-2)',
                fontSize: '.8rem', fontWeight: onlyAvailable ? 600 : 400,
                cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
              }}>
                <span style={{ fontSize: '.75rem' }}>{onlyAvailable ? '✓' : '○'}</span>
                Disponibile azi
              </button>
              {hasActiveFilters && (
                <button onClick={() => { setSearchTerm(''); setSelectedCity('Toate orașele'); setOnlyAvailable(false); }} style={{
                  background: 'none', border: 'none', padding: 0,
                  color: 'var(--tt-text-muted)', fontSize: '.8rem',
                  cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
                }}>Resetează</button>
              )}
            </div>
          </div>
        </div>

        {/* States */}
        {loading && (
          <div className="text-center py-5">
            <span className="spinner-border" style={{ color: 'var(--tt-primary)' }} />
          </div>
        )}
        {!loading && error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <>
            <p className="result-count">
              {filtered.length} {filtered.length !== 1 ? 'cluburi găsite' : 'club găsit'}
              {hasActiveFilters && <span style={{ color: 'var(--tt-accent)', marginLeft: '.4rem', fontWeight: 500 }}>· filtre active</span>}
            </p>

            {filtered.length === 0 ? (
              <div className="tt-empty">
                <div className="tt-empty-icon">🏓</div>
                <h5>Niciun club găsit</h5>
                <p>Încearcă să ajustezi criteriile de căutare</p>
              </div>
            ) : (
              <div className="row g-4">
                {filtered.map(club => {
                  const available = club.number_of_tables - (club.booked_tables_today ?? 0);
                  const hovered = hoveredId === club.id;
                  return (
                    <div key={club.id} className="col-md-6 col-lg-4">
                      <Link
                        to={clubPath(club)}
                        style={{ textDecoration: 'none', display: 'block' }}
                        onMouseEnter={() => setHoveredId(club.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <div className="entity-card" style={{
                          cursor: 'pointer',
                          transform: hovered ? 'translateY(-4px)' : 'none',
                          boxShadow: hovered ? '0 12px 32px rgba(0,0,0,.12)' : undefined,
                          transition: 'transform .2s ease, box-shadow .2s ease',
                        }}>
                          {/* Image */}
                          <div style={{ position: 'relative', height: 190, overflow: 'hidden', background: 'var(--tt-surface-2)', flexShrink: 0 }}>
                            {club.image_url ? (
                              <img src={club.image_url} alt={club.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s ease',
                                  transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.5rem' }}>🏓</div>
                            )}
                            {/* Gradient */}
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.65) 0%, transparent 50%)' }} />
                            {/* Table count badge */}
                            <div style={{
                              position: 'absolute', top: 10, right: 10,
                              background: 'rgba(255,255,255,.93)', borderRadius: 8,
                              padding: '3px 10px', fontSize: '.72rem', fontWeight: 700,
                              color: 'var(--tt-text)', backdropFilter: 'blur(6px)',
                              letterSpacing: '.02em',
                            }}>
                              🏓 {club.number_of_tables} mese
                            </div>
                            {/* Club name overlay */}
                            <div style={{ position: 'absolute', bottom: 10, left: 14, right: 14 }}>
                              <div className="fw-bold" style={{ color: '#fff', fontSize: '1.05rem', textShadow: '0 1px 6px rgba(0,0,0,.5)', lineHeight: 1.25 }}>
                                {club.name}
                              </div>
                              <div style={{ color: 'rgba(255,255,255,.75)', fontSize: '.78rem', marginTop: 2 }}>
                                📍 {club.city_name}
                              </div>
                            </div>
                          </div>

                          {/* Body */}
                          <div className="entity-card-body">
                            {/* Rating row */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              {club.rating != null
                                ? <StarRating rating={club.rating} reviews={club.reviews_count ?? 0} />
                                : <span />
                              }
                              {club.open_hours && (
                                <span style={{ fontSize: '.75rem', color: 'var(--tt-text-muted)' }}>🕐 {club.open_hours}</span>
                              )}
                            </div>

                            {/* Availability */}
                            <div className="mb-3">
                              <AvailabilityBar available={available} total={club.number_of_tables} />
                            </div>

                            {/* Description */}
                            {club.description && (
                              <p style={{ fontSize: '.83rem', color: 'var(--tt-text-2)', lineHeight: 1.55, marginBottom: '.75rem' }}>
                                {club.description.length > 90 ? `${club.description.substring(0, 90)}…` : club.description}
                              </p>
                            )}

                            {/* Amenities */}
                            {club.amenities && club.amenities.length > 0 && (
                              <div className="d-flex flex-wrap gap-1">
                                {club.amenities.map(a => (
                                  <span key={a} style={{
                                    background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
                                    borderRadius: 6, padding: '2px 8px',
                                    fontSize: '.71rem', color: 'var(--tt-text-2)',
                                  }}>{a}</span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* CTA footer */}
                          <div className="entity-card-footer">
                            <div className={`btn btn-primary btn-sm w-100${available > 0 ? '' : ' btn-outline-secondary'}`}
                              style={available === 0 ? { background: 'var(--tt-surface-2)', color: 'var(--tt-text-muted)', border: '1px solid var(--tt-border)' } : {}}>
                              {available > 0 ? 'Rezervă masă →' : 'Vezi detalii →'}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Clubs;
